import {useState, useRef, useEffect} from 'preact/hooks'
import {IconChevron} from "./svg-icons/IconChevron.tsx";
import {DropdownOptions, DropdownPayload, DropdownProps} from "../types/travel.ts"

export function Dropdown({
                             options,
                             icon,
                             onSelectionChange,
                             setDefaultValue = false,
                             selectText = 'Bitte wählen',
                             cssClasses = '',
                             section = '',
                             selectedValue: propSelectedValue
}: DropdownProps) {

    const [isOpen, setIsOpen] = useState(false)
    const [selectedValue, setSelectedValue] = useState<DropdownOptions | null>(null);
    const containerRef = useRef<HTMLDivElement>(null)

    // Update internal selectedValue when prop changes
    useEffect(() => {
        if (propSelectedValue) {
            const option = options.find(opt => opt.value === propSelectedValue);
            if (option) {
                setSelectedValue(option);
            }
        } else if (!setDefaultValue && options.length > 0 && !selectedValue) {
            const defaultOption = options[0];
            setSelectedValue(defaultOption);
            onSelectionChange(defaultOption);
        }
    }, [propSelectedValue, setDefaultValue, options, selectedValue, onSelectionChange]);

    const handleOptionSelect = (section: string, option: DropdownPayload) => {
        const payload = {
            section : section ?? '',
            value: option.value ?? ''
        }
        onSelectionChange(payload);
        setSelectedValue(payload);
        setIsOpen(false)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        const shadowRoot = containerRef.current?.getRootNode() as ShadowRoot | Document

        if (shadowRoot && shadowRoot !== document) {
            // We're in a Shadow DOM
            shadowRoot.addEventListener('mousedown', handleClickOutside)
            return () => shadowRoot.removeEventListener('mousedown', handleClickOutside)
        } else {
            // Regular DOM
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }

    }, [])

    return (
        <div className="relative inline-flex" ref={containerRef}>
            <div
                className={
                    `h-[43px] w-auto px-[14px] py-[14px] rounded-10px
                    border-2 border-theme-gray-light flex items-center
                    gap-2 cursor-pointer whitespace-nowrap ${cssClasses}`
                }
                onClick={() => setIsOpen(!isOpen)}
            >
                {icon ? <span>{icon ?? ''}</span> : ''}
                <span className="text-14px font-semibold leading-none text-nowrap">
                        {selectedValue?.label ? selectedValue.label : selectText}
                    </span>
                <span><IconChevron cssClasses={`h-1.5 ${isOpen ? 'rotate-180' : 'rotate-0'}`} /></span>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="min-w-[260px] absolute border-2 border-solid border-theme-gray top-full w-full right-0 pt-2.5 mt-2 bg-white rounded-10px shadow-filter-box max-h-60 z-50">
                    {options.length > 0 && (
                            <ul className="pb-2.5">
                                {options.map(option => (
                                    <li key={option.value}>
                                        <button
                                            onClick={() => handleOptionSelect(section, option)}
                                            className="w-full text-nowrap flex justify-between items-center px-4 py-2 text-14px text-left hover:font-bold hover:bg-theme-red-light transition-colors"
                                        >
                                            {option.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                </div>
            )}
        </div>
    )
}

